import dayjs from 'dayjs';
import { Utils } from '@gv/triage-components';

import { militaryToAmPm } from 'utils/helpers';
import { TeamAnalytics, TeamAnalyticsUser } from 'types';

import { GroupedCallsListItem } from './types';

export const formatDuration = (value?: number) => {
  const duration = dayjs.duration(value ?? 0, 's');
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  let result = [''];

  if (hours) {
    result.push(`${hours}h`);
  }

  if (minutes) {
    result.push(`${minutes}m`);
  }

  if (!result.length && seconds) {
    result.push(`${seconds}s`);
  }

  return Utils.Helpers.joinStrings(result, ' ', '0');
};

export const formatDurationWithSeconds = (value?: number) => {
  const duration = dayjs.duration(value ?? 0, 's');
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  let result: string[] = [];
  if (hours) {
    result.push(`${hours}h`);
  }

  if (minutes) {
    result.push(`${minutes}m`);
  }

  if (seconds) {
    result.push(`${seconds}s`);
  }

  return Utils.Helpers.joinStrings(result, ' ', '0');
};

export const formatDurationWithoutFloor = (value?: number) => {
  const duration = dayjs.duration(value ?? 0, 's');
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  let result: string[] = [];

  if (hours) {
    result.push(`${hours}h`);
  }
  if (minutes) {
    result.push(`${minutes}m`);
  }
  if (seconds || (!hours && !minutes)) {
    result.push(`${seconds}s`);
  }

  return Utils.Helpers.joinStrings(result, ' ', '0');
};

export const getPaginatedData = (
  groupedItems: GroupedCallsListItem[],
  currentPage: number,
  pageSize: number
) => {
  if (!groupedItems) {
    return {};
  }

  const titles = new Set<string>();

  const paginatedGroupedItems = groupedItems.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  paginatedGroupedItems.forEach(({ user, date }) =>
    Object.keys(user.totalByHour[date]).forEach(titles.add, titles)
  );

  const sortedTimeTitles = Array.from(titles).sort((a, b) => +a - +b);

  return {
    timeTitles: sortedTimeTitles.map(militaryToAmPm),
    paginatedGroupedItems: paginatedGroupedItems.map((item) => ({
      ...item,
      callsByTime: sortedTimeTitles.map(
        (time) => item.user.totalByHour[item.date][time] || 0
      ),
    })),
  };
};

export const groupItemsByDate = (users?: TeamAnalytics['users']) => {
  if (!users) {
    return {
      groupedItems: [],
      totalDurations: {
        logout: 0,
        onCall: 0,
        onChat: 0,
        onBreak: 0,
        onShift: 0,
        coaching: 0,
        notTakingCalls: 0,
      },
    };
  }

  const groupedItems: GroupedCallsListItem[] = [];

  const totalDurations = {
    logout: 0,
    onCall: 0,
    onChat: 0,
    onBreak: 0,
    onShift: 0,
    coaching: 0,
    notTakingCalls: 0,
  };

  users?.forEach((user) => {
    totalDurations.logout += user.logoutCount ?? 0;
    totalDurations.coaching += user.statuses.coaching ?? 0;
    totalDurations.onBreak += user.statuses.break ?? 0;
    totalDurations.onShift += user.totalOnShift ?? 0;
    totalDurations.onCall += user.statuses.call ?? 0;
    totalDurations.onChat += user.statuses.chat ?? 0;

    const totalActiveTime =
      (user.statuses.call ?? 0) +
      (user.statuses.chat ?? 0) +
      (user.statuses.break ?? 0) +
      (user.statuses.coaching ?? 0);

    totalDurations.notTakingCalls += Math.max(
      0,
      Number(user.totalOnShift || 0) - totalActiveTime
    );

    Object.keys(user.totalByHour).forEach((date) => {
      const resultItem = {
        date,
        user,
        totalCallsHandledOnDate: Object.values(user.totalByHour[date]).reduce(
          (acc, current) => acc + current,
          0
        ),
      };

      groupedItems.push(resultItem);
    });
  });

  const sortedGroupedItems = groupedItems.sort((a, b) =>
    dayjs(a.date).isAfter(dayjs(b.date)) ? -1 : 1
  );

  return {
    groupedItems: sortedGroupedItems,
    totalDurations: {
      logout: totalDurations.logout,
      onCall: totalDurations.onCall,
      onChat: totalDurations.onChat,
      onBreak: totalDurations.onBreak,
      onShift: totalDurations.onShift,
      coaching: totalDurations.coaching,
      notTakingCalls: totalDurations.notTakingCalls,
    },
  };
};

export const getNotTakingCallsDuration = (users?: TeamAnalyticsUser[]) => {
  if (!users?.length) {
    return 0;
  }

  const durationOnline = users.reduce(
    (sum, { statuses }) => sum + (statuses?.online ?? 0),
    0
  );
  return formatDuration(durationOnline);
};

export const transformClockInOutData = (data: any[]) => {
  const clockInOutData: { clockIn: string; clockOut: string }[] = [];
  for (let i = 0; i < data.length; i++) {
    if (data[i].status === 'online') {
      clockInOutData.push({
        clockIn: dayjs(data[i].startedAt).format('YYYY-MM-DD hh:mm A'),
        clockOut:
          data[i + 1]?.status === 'offline'
            ? dayjs(data[i + 1].startedAt).format('YYYY-MM-DD hh:mm A')
            : 'Currently On',
      });
    }
  }
  return clockInOutData;
};

export const initialValues = {
  is_test: 'no',
};
